import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from './FloatingSupportWidget.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMessage, faXmark } from '@fortawesome/free-solid-svg-icons';
import SupportCaseAdmin from '~/pages/administrator/AIChatbotPage/SupportCaseAdmin';
import { useAuth } from '~/contexts/AuthContext';

const cx = classNames.bind(styles);

const FloatingSupportWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [pendingCount, setPendingCount] = useState(0);

    // Draggable state
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const isDragging = useRef(false);
    const startPos = useRef({ x: 0, y: 0 });
    const widgetRef = useRef(null);

    const { admin } = useAuth();

    // Check if user has permission to see the widget
    const hasPermission = useMemo(() => {
        if (!admin) return false;
        const roles = admin.Roles || admin.roles || [];
        const roleString = admin.Role || admin.role || '';
        const isSuperAdmin = roleString === 'Super Admin' || roles.includes('Super Admin');
        if (isSuperAdmin) return true;

        const permissions = admin.Permissions || admin.permissions || [];
        return permissions.includes('Permissions.SupportTicket.Access');
    }, [admin]);

    const handleBadgeUpdate = useCallback((unread, pending) => {
        setUnreadCount(unread);
        setPendingCount(pending);
    }, []);

    const toggleOpen = () => {
        setIsOpen(prev => !prev);
    };

    // --- Drag Logic ---
    const handlePointerDown = (e) => {
        // Only accept primary mouse button or touch
        if (e.button !== undefined && e.button !== 0) return;
        
        isDragging.current = false;
        startPos.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
        
        document.addEventListener('pointermove', handlePointerMove);
        document.addEventListener('pointerup', handlePointerUp);
        // Prevent default text selection during drag
        document.body.style.userSelect = 'none';
    };

    const handlePointerMove = (e) => {
        const newX = e.clientX - startPos.current.x;
        const newY = e.clientY - startPos.current.y;
        
        // If moved more than 5px, it's a drag
        if (Math.abs(newX - position.x) > 5 || Math.abs(newY - position.y) > 5) {
            isDragging.current = true;
        }

        // Limit dragging bounds to window
        if (widgetRef.current) {
            const rect = widgetRef.current.getBoundingClientRect();
            // Prevent dragging off screen
            const maxX = window.innerWidth - rect.width;
            const maxY = window.innerHeight - rect.height;
        }

        setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = (e) => {
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.body.style.userSelect = '';
        
        // If we didn't drag, treat it as a click
        if (!isDragging.current) {
            toggleOpen();
        }
    };
    // ------------------

    if (!hasPermission) return null;

    const totalBadge = unreadCount + pendingCount;

    return (
        <div 
            ref={widgetRef}
            className={cx('widgetContainer')} 
            style={{ 
                transform: `translate(${position.x}px, ${position.y}px)`,
                // Add absolute positioning dynamically or rely on CSS translate
            }}
        >
            {/* Popover */}
            <div className={cx('widgetPopover', { open: isOpen })}>
                <div className={cx('widgetHeader')}>
                    <h3 className={cx('title')}>
                        <FontAwesomeIcon icon={faMessage} style={{ color: '#ff9900' }} /> Customer Support
                        {pendingCount > 0 && (
                            <span style={{ fontSize: '11px', backgroundColor: 'rgba(255, 153, 0, 0.2)', color: '#ff9900', padding: '2px 8px', borderRadius: '12px', marginLeft: '8px', fontWeight: 'bold' }}>
                                {pendingCount} waiting
                            </span>
                        )}
                    </h3>
                    <button className={cx('closeBtn')} onClick={toggleOpen} aria-label="Close widget">
                        <FontAwesomeIcon icon={faXmark} size="lg" />
                    </button>
                </div>
                
                <div className={cx('widgetBody')}>
                    {/* Render the admin support component, hidden when closed but mounted to preserve signalR connection */}
                    <div style={{ display: isOpen ? 'block' : 'none', height: '100%' }}>
                        <SupportCaseAdmin isWidget={true} onBadgeUpdate={handleBadgeUpdate} />
                    </div>
                </div>
            </div>

            {/* Floating Action Button */}
            <button 
                className={cx('floatingBtn', { open: isOpen })} 
                onPointerDown={handlePointerDown}
                aria-label="Toggle support chat"
                style={{ touchAction: 'none' }} // Prevent scrolling when touching the button
            >
                {isOpen ? (
                    <FontAwesomeIcon icon={faXmark} size="xl" />
                ) : (
                    <FontAwesomeIcon icon={faMessage} size="xl" />
                )}
                {!isOpen && totalBadge > 0 && (
                    <div className={cx('badge')}>
                        {totalBadge > 99 ? '99+' : totalBadge}
                    </div>
                )}
            </button>
        </div>
    );
};

export default FloatingSupportWidget;
